// ============================================================
// ALBATROS — CardScanModal.tsx
// Captura del codigo de una tarjeta antes de cobrar con ella.
// Lo usan la tarjeta Marcial Pons y la tarjeta/cheque regalo: en el
// TPV de referencia (tpv-albatross-4) son dos modales clonados que
// solo se diferencian en el rotulo, el color y el prefijo del codigo,
// asi que aqui es un solo componente con dos configuraciones.
// ============================================================

import { Camera, CheckCircle2, Keyboard, Loader2, ScanLine, Ticket, Volume2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import './CardScanModal.css';

type CameraStatus = 'loading' | 'granted' | 'failed';

export interface CardScanConfig {
  /** Tema de color del modal; define el acento en el CSS. */
  theme: 'marcial' | 'gift';
  /** Rotulo de la cinta superior. */
  title: string;
  icon: React.ReactNode;
  /** Prefijo del codigo de esta tarjeta, para el codigo de prueba. */
  prefix: string;
  /** Las dos letras que esta tarjeta añade al teclado numerico. */
  letterKeys: [string, string];
  placeholder: string;
  formatHint: string;
}

export const MARCIAL_CARD: CardScanConfig = {
  theme: 'marcial',
  title: 'Cobro con Tarjeta Marcial Pons',
  icon: <ScanLine size={24} />,
  prefix: 'MP',
  letterKeys: ['M', 'P'],
  placeholder: 'Por ej. MP-94283120',
  formatHint: 'El formato habitual es MP- seguido del número'
};

export const GIFT_CARD: CardScanConfig = {
  theme: 'gift',
  title: 'Cobro con Tarjeta o Cheque Regalo',
  icon: <Ticket size={24} />,
  prefix: 'CR',
  letterKeys: ['C', 'R'],
  placeholder: 'Por ej. CR-90184820',
  formatHint: 'El formato habitual es CR- seguido del número'
};

/** El teclado tactil: numeros, las dos letras de la tarjeta y el guion. */
const buildKeys = (letters: [string, string]): string[] => [
  '1', '2', '3', letters[0], 'CLR',
  '4', '5', '6', letters[1], '⌫',
  '7', '8', '9', '-', '0'
];

const keyClass = (key: string, letters: [string, string]): string => {
  if (key === 'CLR') return ' cscan-key--clear';
  if (key === '⌫') return ' cscan-key--back';
  if (key === '-' || key === letters[0] || key === letters[1]) return ' cscan-key--letter';
  return '';
};

interface CardScanModalProps {
  config: CardScanConfig;
  onClose: () => void;
  /** Recibe el codigo leido o tecleado; el cobro lo sigue el llamante. */
  onConfirm: (code: string) => void;
}

export function CardScanModal({ config, onClose, onConfirm }: CardScanModalProps) {
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('loading');
  const [code, setCode] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmTimer = useRef<number | null>(null);

  const keys = buildKeys(config.letterKeys);

  /** Pitido corto de confirmacion de lectura, como el del lector de codigos. */
  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1000, ctx.currentTime);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.12);
    } catch (e) {
      // La lectura se confirma igual en pantalla, pero el fallo tiene que
      // verse: si deja de crearse el contexto, el pitido se apaga sin mas.
      console.warn('AudioContext scan beep fail', e);
    }
  };

  // La camara es solo vista previa: no descodifica nada. El lector fisico de la
  // caja se comporta como un teclado, asi que escribe en el input de abajo, que
  // por eso arranca con el foco puesto.
  useEffect(() => {
    let stream: MediaStream | null = null;
    let isMounted = true;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraStatus('failed');
    } else {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .then(granted => {
          if (!isMounted) {
            granted.getTracks().forEach(track => track.stop());
            return;
          }
          stream = granted;
          setCameraStatus('granted');
          if (videoRef.current) videoRef.current.srcObject = granted;
        })
        .catch(() => {
          if (isMounted) setCameraStatus('failed');
        });
    }

    if (inputRef.current) inputRef.current.focus();

    return () => {
      isMounted = false;
      if (stream) stream.getTracks().forEach(track => track.stop());
      if (confirmTimer.current !== null) window.clearTimeout(confirmTimer.current);
    };
  }, []);

  const confirm = (value: string, delayMs: number) => {
    playBeep();
    setCode(value);
    setIsSuccess(true);
    confirmTimer.current = window.setTimeout(() => onConfirm(value), delayMs);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuccess || !code.trim()) return;
    confirm(code.trim(), 400);
  };

  /** Genera un codigo de prueba: no hay lectura real de codigos de barras. */
  const handleSimulate = () => {
    if (isSuccess) return;
    confirm(`${config.prefix}-${Math.floor(10000000 + Math.random() * 90000000)}`, 1800);
  };

  const handleKey = (key: string) => {
    if (isSuccess) return;
    if (key === 'CLR') setCode('');
    else if (key === '⌫') setCode(prev => prev.slice(0, -1));
    else setCode(prev => prev + key);
    if (inputRef.current) inputRef.current.focus();
  };

  // ── Render ───────────────────────────────────────────────

  return (
    <div className={`cscan-root cscan-root--${config.theme}`}>
      <div className="cscan-backdrop" onClick={isSuccess ? undefined : onClose} />
      <div className="cscan-card">

        <div className="cscan-ribbon">
          <span className="cscan-ribbon-icon">{config.icon}</span>
          <span className="cscan-ribbon-text">{config.title}</span>
          <button onClick={onClose} className="cscan-close" title="Cancelar" disabled={isSuccess}>
            <X size={24} />
          </button>
        </div>

        <div className="cscan-body">

          {/* Columna izquierda: vista previa de la camara */}
          <div className="cscan-col-scanner">
            <span className="cscan-label">Escáner inteligente / cámara</span>

            <div className="cscan-viewport">
              {cameraStatus === 'loading' && (
                <div className="cscan-viewport-state">
                  <Loader2 size={40} className="cscan-spin" />
                  <p className="cscan-viewport-hint">Cargando cámara...</p>
                </div>
              )}

              {cameraStatus === 'failed' && (
                <div className="cscan-viewport-state">
                  <div className="cscan-cam-off"><Camera size={24} /></div>
                  <p className="cscan-viewport-title">Cámara no disponible</p>
                  <p className="cscan-viewport-hint">
                    Usa el lector de la caja o teclea el código a mano.
                  </p>
                </div>
              )}

              {cameraStatus === 'granted' && (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`cscan-video${isSuccess ? ' cscan-video--dim' : ''}`}
                  />
                  {!isSuccess && <div className="cscan-laser" />}
                  <div className="cscan-reticle">
                    <span className="cscan-corner cscan-corner--tl" />
                    <span className="cscan-corner cscan-corner--tr" />
                    <span className="cscan-corner cscan-corner--bl" />
                    <span className="cscan-corner cscan-corner--br" />
                  </div>
                  {!isSuccess && (
                    <div className="cscan-viewport-badge">Vista previa — no lee el código</div>
                  )}
                </>
              )}

              {isSuccess && (
                <div className="cscan-success">
                  <div className="cscan-success-icon"><CheckCircle2 size={40} /></div>
                  <h4 className="cscan-success-title">Lectura completa</h4>
                  <p className="cscan-success-code">Código: {code}</p>
                  <p className="cscan-success-note">Registrando transacción...</p>
                </div>
              )}
            </div>

            {!isSuccess && (
              <button type="button" onClick={handleSimulate} className="cscan-simulate">
                <Volume2 size={16} />
                Simular escaneo (código de prueba)
              </button>
            )}
          </div>

          {/* Columna derecha: entrada manual y teclado tactil */}
          <form className="cscan-col-input" onSubmit={handleSubmit}>
            <label className="cscan-label" htmlFor="cscan-code">
              Introducción del código (lector o teclado)
            </label>

            <div className="cscan-input-wrap">
              <Keyboard size={20} className="cscan-input-icon" />
              <input
                id="cscan-code"
                ref={inputRef}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isSuccess}
                placeholder={config.placeholder}
                autoComplete="off"
                className="cscan-input"
              />
              {code && !isSuccess && (
                <button type="button" onClick={() => setCode('')} className="cscan-input-clear">
                  LIMPIAR
                </button>
              )}
            </div>

            <div className="cscan-keypad">
              {keys.map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleKey(key)}
                  disabled={isSuccess}
                  className={`cscan-key${keyClass(key, config.letterKeys)}`}
                >
                  {key}
                </button>
              ))}
            </div>

            <p className="cscan-format-hint">{config.formatHint}</p>

            <div className="cscan-actions">
              <button type="button" onClick={onClose} disabled={isSuccess} className="cscan-btn-cancel">
                CANCELAR
              </button>
              <button type="submit" disabled={isSuccess || !code.trim()} className="cscan-btn-confirm">
                CONFIRMAR COBRO
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}
